import React from "react";
import { Auth0Provider } from "@auth0/auth0-react";
import { AuthProvider as OidcAuthProvider } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { auth0Config, isOpenIddict, openIddictConfig } from "./authConfig";

interface BeamXRAuthProviderProps {
  children: React.ReactNode;
}

/**
 * Mounts whichever identity provider the build is configured for.
 *
 * Everything below this component talks to {@link useBeamXRAuth} rather than either library
 * directly, so swapping providers does not ripple through the app.
 */
export default function BeamXRAuthProvider({ children }: BeamXRAuthProviderProps) {
  if (isOpenIddict) {
    return (
      <OidcAuthProvider
        authority={openIddictConfig.authority}
        client_id={openIddictConfig.clientId}
        redirect_uri={openIddictConfig.redirectUri}
        post_logout_redirect_uri={openIddictConfig.postLogoutRedirectUri}
        scope={openIddictConfig.scope}
        response_type="code"
        // Renew in the background off the refresh token, so a one hour access token is
        // invisible to the user.
        automaticSilentRenew={true}
        // Session storage rather than local storage keeps tokens out of other tabs and clears
        // them when the tab closes.
        userStore={new WebStorageStateStore({ store: window.sessionStorage })}
        onSigninCallback={() => {
          // Strip the authorization code and state from the address bar so a refresh does not
          // try to redeem an already used code.
          window.history.replaceState({}, document.title, "/");
        }}
      >
        {children}
      </OidcAuthProvider>
    );
  }

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: auth0Config.scope,
        audience: auth0Config.audience,
      }}
    >
      {children}
    </Auth0Provider>
  );
}
